import { WhiteboardStatus } from "@src/components/board/queries/query-keys";
import { UserActivationStatus, WhiteboardPermission } from "@src/gql/graphql";
import z from "zod";

const TrailUserSchema = z.object({
	clientId: z.string(),
	email: z.string(),
	userId: z.string(),
});

const DocumentTrailSchema = z.object({
	createdAt: z.string().transform((val) => new Date(val)),
	updatedAt: z.string().transform((val) => new Date(val)),
	createdBy: TrailUserSchema.nullable(),
	updatedBy: TrailUserSchema.nullable(),
});

const PublicationSchema = z.object({
	allowClone: z.boolean().nullable().optional(),
	isPublished: z.boolean(),
	isReadonly: z.boolean().nullable().optional(),
	key: z.string().nullable().optional(),
});

const UserDetailsSchema = z.object({
	activationStatus: z.nativeEnum(UserActivationStatus),
	avatarUrl: z.string().nullable().optional(),
	created: z.string().nullable().optional(),
	displayName: z.string().nullable().optional(),
	domain: z.string().nullable().optional(),
	email: z.string(),
	firstName: z.string().nullable().optional(),
	language: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	team: z.string().nullable().optional(),
	timezone: z.string().nullable().optional(),
});

const SimpleCollaborationSchema = z.object({
	isShared: z.boolean(),
	collaborators: z.array(
		z.object({
			permission: z.nativeEnum(WhiteboardPermission),
			userId: z.string().nullable(),
			userEmail: z.string(),
		}),
	),
});

const DetailedCollaborationSchema = z.object({
	isShared: z.boolean(),
	collaborators: z.array(
		z.object({
			permission: z.nativeEnum(WhiteboardPermission),
			userId: z.string().nullable(),
			userEmail: z.string(),
			details: UserDetailsSchema.nullable().optional(),
		}),
	),
});

const RankingItemSchema = z.object({
	listId: z.string(),
	itemKey: z.string(),
	itemRank: z.string().nullable().optional(),
	itemDescription: z.string().nullable().optional(),
});

const IntakeSchema = z.object({
	isOpen: z.boolean(),
	key: z.string().nullable().optional(),
});

export const WhiteboardBaseSchema = z.object({
	id: z.string(),
	name: z.string(),
	ownerId: z.string(),
	permission: z.nativeEnum(WhiteboardPermission),
	position: z.number(),
	publication: PublicationSchema.nullable().optional(),
	status: z.nativeEnum(WhiteboardStatus),
	streamPosition: z.number(),
	intake: IntakeSchema.nullable().optional(),
});

export const WhiteboardSchema = WhiteboardBaseSchema.extend({
	collaboration: SimpleCollaborationSchema.nullable().optional(),
	trail: DocumentTrailSchema,
	ranking: z.array(RankingItemSchema),
	publication: PublicationSchema.nullable().optional(),
});

export const WhiteboardWithUsersSchema = WhiteboardBaseSchema.extend({
	collaboration: DetailedCollaborationSchema.nullable().optional(),
});

export const WhiteboardListingSchema = z.array(WhiteboardSchema);

export const TrashItemLocationSchema = z.object({
	containerId: z.string().nullish(),
	containerName: z.string().nullish(),
	locationType: z.string(),
	path: z.string().nullish(),
});

export const TrashItemSchema = z.object({
	deletedAt: z.string(),
	deletedBy: z.string(),
	entityId: z.string(),
	id: z.string(),
	name: z.string(),
	ownerEmail: z.string(),
	ownerId: z.string(),
	position: z.number(),
	streamPosition: z.number(),
	location: TrashItemLocationSchema,
});

export const TrashItemListingSchema = z.array(TrashItemSchema);

export type WhiteboardBase = z.infer<typeof WhiteboardBaseSchema>;
export type Whiteboard = z.infer<typeof WhiteboardSchema>;
export type WhiteboardWithUsers = z.infer<typeof WhiteboardWithUsersSchema>;
export type WhiteboardListing = z.infer<typeof WhiteboardListingSchema>;
export type Collaborators = z.infer<
	typeof DetailedCollaborationSchema
>["collaborators"];
export type RankingItem = z.infer<typeof RankingItemSchema>;
export type Publication = z.infer<typeof PublicationSchema>;
export type SimpleCollaboration = z.infer<typeof SimpleCollaborationSchema>;
export type DetailedCollaboration = z.infer<typeof DetailedCollaborationSchema>;
export type TrashItem = z.infer<typeof TrashItemSchema>;
export type TrashItemLocation = z.infer<typeof TrashItemLocationSchema>;
